/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  GamoraFoundationGlue,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import { IHoruz } from '../horuz.js';
const debug = debugCreate({ namespace: 'horuz:queues:data' });

/**
 * Children map
 * @public
 */
export interface HoruzQueueDataNodeChildrenMap<TRoot extends IHoruz> {}

/**
 * Args
 * @public
 */
export interface HoruzQueueDataNodeArgs<
  TRoot extends IHoruz,
> extends GamoraFoundationNodeArgs<HoruzQueueDataNodeChildrenMap<TRoot>> {
  defaultMaxCollectionsPerNamespace?: number;
  defaultMaxNamespaces?: number;
}

/**
 * @public
 */
export type HoruzQueueDataConfiguration = {
  defaultMaxCollectionsPerNamespace: number;
  defaultMaxNamespaces: number;
};

/**
 * @public
 */
export type HoruzQueueDataNextResult =
  | {
      value: Uint8Array;
      done: false;
    }
  | {
      done: true;
    };

/**
 * HoruzQueueDataNode
 * @public
 */
export class HoruzQueueDataNode<
  TRoot extends IHoruz,
> extends GamoraFoundationGlue<HoruzQueueDataNodeChildrenMap<TRoot>, TRoot> {
  /**
   * Default configuration
   * @public
   */
  private _configuration: HoruzQueueDataConfiguration = {
    defaultMaxCollectionsPerNamespace: 10,
    defaultMaxNamespaces: 1000 * 10,
  };

  private _collectionMap: Map<string, Uint8Array[]>;
  constructor(args: HoruzQueueDataNodeArgs<TRoot>) {
    super(args);

    // Initialize map
    this._collectionMap = new Map();

    // Initialize
    this._initialize(args);
  }

  /**
   * Initialize
   * @param args Args
   */
  private _initialize(args: HoruzQueueDataNodeArgs<TRoot>) {
    if (args.defaultMaxCollectionsPerNamespace) {
      this._configuration.defaultMaxCollectionsPerNamespace =
        args.defaultMaxCollectionsPerNamespace;
    }
    if (args.defaultMaxNamespaces) {
      this._configuration.defaultMaxNamespaces = args.defaultMaxNamespaces;
    }
  }

  /**
   * Registers event handler
   * @param namespace Namespace
   * @param name The name
   * @param listener The listener
   * @returns this
   */
  override $on(
    namespace: string,
    name: string,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    super.$on(namespace, name, listener);

    // Override settings
    switch (name) {
      case 'next': {
        const collection = this._collectionMap.get(namespace);

        // Emit one time for every element already in the collection. This
        // ensures that all elements that weren't handled by previous listeners
        // have a chance to be handled.
        if (collection?.length) {
          for (let i = 0; i < collection.length; i++) {
            listener(namespace);
          }
        }
        break;
      }
    }

    return this;
  }

  /**
   * Queues a data
   * @param namespace The namespace
   * @param data The data to queue
   */
  queue(namespace: string, data: Uint8Array): boolean {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );
    assert(
      data instanceof Uint8Array && data.byteLength > 0,
      'Invalid data argument: not a valid Uint8Array.',
    );

    // Get collection in namespace
    let collection = this._collectionMap.get(namespace);
    if (!collection) {
      if (this._collectionMap.size > this._configuration.defaultMaxNamespaces) {
        debug.log(
          '(queue): Cannot add new data to queue (defaultMaxNamespaces exceeded: ' +
            this._collectionMap.size +
            ').',
        );
        return false;
      }

      // Create queue
      collection = [];

      // Set map
      this._collectionMap.set(namespace, collection);
    }

    // Limits on collection in queue
    if (
      collection.length > this._configuration.defaultMaxCollectionsPerNamespace
    ) {
      debug.log(
        '(queue): Cannot add new data to queue (defaultMaxCollectionsPerNamespace exceeded: ' +
          collection.length +
          ').',
      );
      return false;
    }

    // Add data to queue
    collection.push(data);

    // Emit next event
    this.$emit(namespace, 'next', namespace);

    // Added
    return true;
  }

  /**
   * Gets the next data for namespace
   * @param namespace The namespace
   */
  next(namespace: string): HoruzQueueDataNextResult {
    assert(
      typeof namespace === 'string' && namespace.length > 0,
      'Invalid namespace argument: not a valid string.',
    );

    // Get collection for namespace
    const collection = this._collectionMap.get(namespace);
    if (!collection) {
      // No more values
      return {
        done: true,
      };
    }

    // Get data
    const value = collection.shift();

    // Return
    if (value) {
      return {
        done: false,
        value: value,
      };
    } else {
      // Clear
      this.empty(namespace);

      // No more values
      return {
        done: true,
      };
    }
  }

  /**
   * Empties queue for namespace
   * @param namespace
   */
  empty(namespace: string) {
    this._collectionMap.delete(namespace);
  }

  /**
   * Clear all
   */
  clear() {
    this._collectionMap = new Map();
  }
}
